import React, { useEffect, useState, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import { getIconFileName } from '../../utils/iconUtils';
import { useSeriesCategories } from '../../hooks/useSeriesCategories';
import CategoryIcon from '../CategoryIcon';

const API_URL = 'https://iracing6-backend.herokuapp.com';

function SeriesPopularityWidget({ config, expandState = 0, onConfigChange }) {
  const ITEMS_PER_PAGE = expandState === 2 ? 8 : 5;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);

  // Filter state (initialized from persisted config)
  const [strengthRange, setStrengthRange] = useState(config.strengthRange || [0, 99999]);
  const [startTimesRange, setStartTimesRange] = useState(config.startTimesRange || [0, 24]);
  const [filter, setFilter] = useState(config.categoryFilter || []);
  const [licenseFilter, setLicenseFilter] = useState(config.licenseFilter || []);
  const [wetTrack, setWetTrack] = useState(config.wetTrack || false);
  const [weekNumber, setWeekNumber] = useState(0);
  const [weekNumberSelected, setWeekNumberSelected] = useState(
    config.weekNumberSelected !== undefined ? config.weekNumberSelected : null
  );
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const isInitialMount = useRef(true);
  const [chartElement, setChartElement] = useState(null);
  const [chartWidth, setChartWidth] = useState(300);
  useEffect(() => {
    if (!chartElement) return;
    const observer = new ResizeObserver(([entry]) => setChartWidth(entry.contentRect.width));
    observer.observe(chartElement);
    return () => observer.disconnect();
  }, [chartElement]);
  const seriesCategoryMap = useSeriesCategories();

  // Fetch current week number
  useEffect(() => {
    async function fetchWeekNumber() {
      try {
        const response = await fetch(`${API_URL}/api/week-number`);
        const jsonData = await response.json();
        const numericWeek = parseInt(jsonData, 10);
        setWeekNumber(numericWeek);
        // Only set selected week if not restored from config
        if (config.weekNumberSelected === undefined) {
          setWeekNumberSelected(numericWeek);
        }
      } catch (error) {
        console.error('Error fetching week number:', error);
      }
    }
    fetchWeekNumber();
  }, []);

  // Fetch available historic weeks
  useEffect(() => {
    async function fetchAvailableWeeks() {
      try {
        const response = await fetch(`${API_URL}/api/sof-results/historic-weeks`);
        const weeks = await response.json();
        setAvailableWeeks(weeks);
      } catch (error) {
        console.error('Error fetching available weeks:', error);
      }
    }
    fetchAvailableWeeks();
  }, []);

  // Fetch data based on selected week
  useEffect(() => {
    if (weekNumberSelected === null || weekNumber === 0) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        let endpoint;
        if (weekNumberSelected === 0) {
          endpoint = `${API_URL}/api/sof-results/full-season`;
        } else if (weekNumberSelected === weekNumber) {
          endpoint = `${API_URL}/api/sof-results/sofresults`;
        } else {
          endpoint = `${API_URL}/api/sof-results/historic/${weekNumberSelected}`;
        }

        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Failed to fetch data');
        const result = await response.json();

        // Convert start_time to UTC hours
        result.forEach((item) => {
          const startTime = new Date(item.start_time);
          item.start_time = startTime.getUTCHours();
        });

        setData(result);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [weekNumberSelected, weekNumber]);

  // Apply filters and compute seasonsData
  const seasonsData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Apply category filter
    let filtered = [...data];
    if (filter.length > 0 && Object.keys(seriesCategoryMap).length > 0) {
      filtered = filtered.filter((item) => {
        const category = seriesCategoryMap[item.season_name];
        return filter.includes(category);
      });
    }

    // Apply wet track filter
    if (wetTrack) {
      filtered = filtered.filter((item) => item.precip_time_pct > 0);
    }

    // Apply license filter
    if (licenseFilter.length > 0) {
      filtered = filtered.filter((item) => licenseFilter.includes(item.license_class));
    }

    // Apply SOF range and hour of day filters
    const isTimeInRange = (startTime, rangeStart, rangeEnd) => {
      if (rangeStart <= rangeEnd) {
        return startTime >= rangeStart && startTime <= rangeEnd;
      } else {
        return startTime >= rangeStart || startTime <= rangeEnd;
      }
    };

    filtered = filtered.filter((item) =>
      item.event_strength_of_field >= strengthRange[0] &&
      item.event_strength_of_field <= strengthRange[1] &&
      isTimeInRange(item.start_time, startTimesRange[0], startTimesRange[1])
    );

    // Process data to get series by average population
    const seasonSessions = {};
    const sessionPopulation = {};
    const seasonTrackMap = {};

    filtered.forEach((item) => {
      const { season_name, session_id, population, track_name } = item;
      if (!season_name) return;

      if (season_name in seasonSessions) {
        seasonSessions[season_name].push(session_id);
      } else {
        seasonSessions[season_name] = [session_id];
      }

      if (session_id in sessionPopulation) {
        sessionPopulation[session_id] += population;
      } else {
        sessionPopulation[session_id] = population;
      }

      if (!seasonTrackMap[season_name]) {
        seasonTrackMap[season_name] = track_name;
      }
    });

    const result = [];
    for (let season in seasonSessions) {
      const sessionIds = [...new Set(seasonSessions[season])];
      let totalPopulation = 0;
      for (let sessionId of sessionIds) {
        totalPopulation += sessionPopulation[sessionId] || 0;
      }
      result.push({
        season,
        avgPopulation: totalPopulation / sessionIds.length,
        trackName: seasonTrackMap[season] || 'Unknown',
      });
    }

    result.sort((a, b) => b.avgPopulation - a.avgPopulation);
    return result;
  }, [data, filter, licenseFilter, wetTrack, strengthRange, startTimesRange, seriesCategoryMap]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [filter, licenseFilter, wetTrack, strengthRange, startTimesRange, weekNumberSelected]);

  // Persist filter state to backend config
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (onConfigChange) {
      onConfigChange({
        ...config,
        strengthRange,
        startTimesRange,
        categoryFilter: filter,
        licenseFilter,
        wetTrack,
        weekNumberSelected,
      });
    }
  }, [strengthRange, startTimesRange, filter, licenseFilter, wetTrack, weekNumberSelected]);

  const toggleFilter = (category) => {
    setFilter((prev) => prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]);
  };

  const toggleLicense = (license) => {
    setLicenseFilter((prev) => prev.includes(license) ? prev.filter((l) => l !== license) : [...prev, license]);
  };

  if (loading || weekNumberSelected === null) {
    return <div className="widget-loading"><div className="spinner-small"></div></div>;
  }

  if (error) {
    return <div className="widget-error">Error: {error}</div>;
  }

  if (!data) {
    return <div className="widget-error">No data available</div>;
  }

  const totalPages = Math.ceil(seasonsData.length / ITEMS_PER_PAGE);
  const pageData = seasonsData.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  const trimSeasonName = (name) => {
    return name
      .replace(/\d{4}\s+Season\s+\d+/g, '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/Fixed Fixed/g, 'Fixed')
      .trim();
  };

  const getIconUrl = (seasonName) => {
    const iconName = getIconFileName(seasonName);
    return `${process.env.PUBLIC_URL}/assets/series-icons/${iconName}.png`;
  };

  const yLabels = pageData.map(item => trimSeasonName(item.season));

  const plotData = [{
    type: 'bar',
    x: pageData.map(item => item.avgPopulation),
    y: yLabels,
    orientation: 'h',
    marker: {
      color: '#3d3d3d',
      line: {
        color: '#7d7d7d',
        width: 1,
      },
    },
    text: pageData.map(item => expandState === 2
      ? `${trimSeasonName(item.season)} - ${item.trackName}`
      : item.trackName),
    textposition: 'inside',
    textfont: {
      size: 12,
      color: '#ddd',
    },
    insidetextanchor: 'end',
    hovertemplate: '%{x:.0f} avg entrants<br>%{text}<extra></extra>',
  }];

  // Adjust layout based on expand state
  const getLeftMargin = () => {
    switch (expandState) {
      case 1: return 50;  // double wide
      case 2: return 60;  // full width
      default: return 45; // normal
    }
  };

  // Create images array for series icons
  const images = pageData.map((item, index) => {
    const imageUrl = getIconUrl(item.season);
    return {
      source: imageUrl,
      xref: 'paper',
      yref: 'y',
      x: -0.01,
      y: yLabels[index],
      xanchor: 'right',
      yanchor: 'middle',
      sizex: 36 / Math.max(1, chartWidth - getLeftMargin() - 10),
      sizey: 0.45,
      sizing: 'contain',
      layer: 'above',
    };
  });

  const layout = {
    margin: { l: getLeftMargin(), r: 10, t: 5, b: 5 },
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent',
    images: images,
    xaxis: {
      title: '',
      color: '#aaa',
      gridcolor: '#333',
      fixedrange: true,
      showticklabels: false,
    },
    yaxis: {
      title: '',
      autorange: 'reversed',
      color: '#aaa',
      showticklabels: false,
      fixedrange: true,
    },
    font: {
      family: 'Questrial, sans-serif',
      size: 12,
      color: '#ddd',
    },
    showlegend: false,
    autosize: true,
    bargap: 0.15,
  };

  return (
    <div className="sp-widget">
      <div className="spw-filters">
        <div className="spw-filter-row">
          <div className="spw-cat-group">
            {['sports_car', 'formula_car', 'oval', 'dirt_road', 'dirt_oval'].map((cat) => (
              <button
                key={cat}
                className={`spw-toggle-btn ${filter.includes(cat) ? 'spw-toggle-active' : ''}`}
                onClick={() => toggleFilter(cat)}
                title={cat.replace(/_/g, ' ')}
              >
                <CategoryIcon category={cat} size={14} />
              </button>
            ))}
          </div>
          <div className="spw-separator" />
          <div className="spw-license-group">
            {[
              { key: 'A', color: '#3498db' },
              { key: 'B', color: '#2ecc71' },
              { key: 'C', color: '#f1c40f' },
              { key: 'D', color: '#e67e22' },
              { key: 'Rookie', color: '#e74c3c', label: 'R' },
            ].map(({ key, color, label }) => (
              <button
                key={key}
                className={`spw-toggle-btn ${licenseFilter.includes(key) ? 'spw-toggle-active' : ''}`}
                onClick={() => toggleLicense(key)}
                style={{ color }}
              >
                {label || key}
              </button>
            ))}
          </div>
          <div className="spw-separator" />
          <label className="spw-rain-label">
            <input
              type="checkbox"
              checked={wetTrack}
              onChange={() => setWetTrack(!wetTrack)}
            />
            Rain
          </label>
        </div>
        <div className="spw-filter-row">
          <span className="spw-filter-label">SOF:</span>
          <input
            type="number"
            className="spw-num-input"
            value={strengthRange[0]}
            onChange={(e) => setStrengthRange([parseInt(e.target.value, 10) || 0, strengthRange[1]])}
          />
          <span className="spw-filter-dash">-</span>
          <input
            type="number"
            className="spw-num-input"
            value={strengthRange[1]}
            onChange={(e) => setStrengthRange([strengthRange[0], parseInt(e.target.value, 10) || 99999])}
          />
          <div className="spw-separator" />
          <span className="spw-filter-label">Hour:</span>
          <input
            type="number"
            className="spw-num-input"
            value={startTimesRange[0]}
            min={0}
            max={23}
            onChange={(e) => setStartTimesRange([parseInt(e.target.value, 10) || 0, startTimesRange[1]])}
          />
          <span className="spw-filter-dash">-</span>
          <input
            type="number"
            className="spw-num-input"
            value={startTimesRange[1]}
            min={0}
            max={24}
            onChange={(e) => setStartTimesRange([startTimesRange[0], parseInt(e.target.value, 10) || 24])}
          />
          <div className="spw-separator" />
          <span className="spw-filter-label">Week:</span>
          <select
            className="spw-week-select"
            value={weekNumberSelected ?? ''}
            onChange={(e) => setWeekNumberSelected(parseInt(e.target.value, 10))}
          >
            <option value={0}>All</option>
            {availableWeeks.map((week) => (
              <option key={week} value={week}>{week}</option>
            ))}
            {weekNumber && !availableWeeks.includes(weekNumber) && (
              <option key={weekNumber} value={weekNumber}>{weekNumber}</option>
            )}
          </select>
        </div>
      </div>
      <div className="sp-widget-chart" ref={setChartElement}>
        <Plot
          data={plotData}
          layout={layout}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </div>
      <div className="sp-widget-nav">
        <button
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
          className="sp-widget-nav-btn"
        >
          &lt;
        </button>
        <span className="sp-widget-page">{page + 1} / {totalPages}</span>
        <button
          onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
          className="sp-widget-nav-btn"
        >
          &gt;
        </button>
      </div>
    </div>
  );
}

export default SeriesPopularityWidget;
